import React, {useEffect, useState} from 'react';
import {DocumentTextIcon, PencilSquareIcon, PlusCircleIcon, TrashIcon, PaperClipIcon} from '@heroicons/react/24/outline';
import {Dialog, DialogPanel, DialogTitle} from '@headlessui/react';
import {formatCurrency, formatDate} from '../../utils';
import {getFileUrl} from '../../utils/api';
import ConfirmationDialog from './ConfirmationDialog';
import DateSelector from './DateSelector';
import FileUploader from './FileUploader';

function ContractModal({ 
  isOpen, 
  onClose, 
  contract, 
  onSave, 
  title = "Contract Details",
  shortName,
  subcontractor,
  isCreatingNew
}) {
  // Initialize state with default values using local timezone for date
  const today = new Date();
  const localDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  
  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    date: localDate,
    subcontractor: subcontractor || '',
    files: []
  });
  
  // Track original files (for edit operations) to identify removed files
  const [originalFiles, setOriginalFiles] = useState([]);
  // Track files that were removed during this edit session
  const [removedFiles, setRemovedFiles] = useState([]);
  
  // Update form data when contract prop changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (contract) {
      // Fix date timezone issue by preserving exactly what came from the server
      // without any Date conversion which can cause timezone issues
      let dateValue = contract.date;
      if (dateValue && typeof dateValue === 'string') {
        // Ensure we get YYYY-MM-DD format without timezone issues
        // Just take the date part (before T or just the whole string if no T)
        dateValue = dateValue.split('T')[0];
      }
      
      // Save original files for tracking removed files
      const contractFiles = contract.files || [];
      setOriginalFiles(contractFiles.map(file => ({...file})));
      
      // Reset removed files when opening a new contract
      setRemovedFiles([]);

      setFormData({
        ...contract,
        // Ensure we have the right data types
        id: contract.id, // Preserve the ID
        amount: contract.amount || '',
        description: contract.description || '',
        subcontractor: contract.subcontractor || subcontractor || '',
        date: dateValue || localDate,
        files: contractFiles
      });
    } else {
      // Reset form for new contracts
      const today = new Date();
      const localDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      
      setFormData({
        amount: '',
        description: '',
        date: localDate,
        subcontractor: isCreatingNew ? subcontractor || '' : '',
        files: []
      });
      setOriginalFiles([]);
      setRemovedFiles([]);
    }
  }, [contract, subcontractor, isOpen, isCreatingNew]);
  
  const [error, setError] = useState('');
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'amount' ? 
        (value === '' ? '' : Number(value)) : 
        value
    });
  };
  
  // Handle file changes, track removed files
  const handleFilesChange = (newFiles) => {
    // Identify which files were removed in this operation
    const currentFileIds = formData.files.map(file => file.uuid);
    const newFileIds = newFiles.map(file => file.uuid);
    
    // Find files that were in currentFileIds but not in newFileIds
    const justRemovedFileIds = currentFileIds.filter(id => !newFileIds.includes(id));
    
    // Add to removed files if they were part of the original files
    const originalFileIds = originalFiles.map(file => file.uuid);
    const removedOriginalFiles = justRemovedFileIds.filter(id => originalFileIds.includes(id));
    
    if (removedOriginalFiles.length > 0) {
      setRemovedFiles(prev => [...prev, ...removedOriginalFiles]);
    }
    
    // Update form data with new files
    setFormData({
      ...formData,
      files: newFiles
    });
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate the form
    if (!formData.amount) {
      setError('Amount is required');
      return;
    }
    
    if (isNaN(Number(formData.amount))) {
      setError('Amount must be a valid number');
      return;
    }
    
    // Clear any errors
    setError('');
    
    // Ensure ID is preserved if this is an edit operation
    const dataToSave = {...formData};
    if (contract && contract.id) {
      dataToSave.id = contract.id;
      console.log("Preserving contract ID:", contract.id);
    }
    
    // Add the list of files that need to be deleted on the server
    if (removedFiles.length > 0) {
      dataToSave.filesToDelete = removedFiles;
    }
    
    // Call the save function
    onSave(dataToSave);
    
    // Close the modal
    onClose();
  };
  
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-10">
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 transition-opacity" />
      
      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
            <div className="absolute top-0 right-0 pt-4 pr-4">
              <button
                type="button"
                className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                onClick={onClose}
              >
                <span className="sr-only">Close</span>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div>
              <div className="sm:mt-5">
                <DialogTitle as="h2" className="text-base/7 font-semibold text-gray-900 dark:text-gray-100">
                  {title}
                </DialogTitle>
                
                {error && (
                  <div className="mt-2 rounded-md bg-red-50 dark:bg-red-900/50 p-4 text-sm text-red-700 dark:text-red-300">
                    {error}
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="mt-6">
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="amount" className="block text-sm/6 font-medium text-gray-900 dark:text-gray-300">
                        Amount (required)
                      </label>
                      <div className="mt-2">
                        <div className="relative">
                          <input
                            type="number"
                            name="amount"
                            id="amount"
                            value={formData.amount}
                            onChange={handleInputChange}
                            className="block w-full rounded-md bg-white dark:bg-gray-700 px-3 py-1.5 pl-8 pr-12 text-base text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                            placeholder="0.00"
                            required
                          />
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <span className="text-gray-500 dark:text-gray-400 sm:text-sm/6">$</span>
                          </div>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                            <span className="text-gray-500 dark:text-gray-400 sm:text-sm/6">USD</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="description" className="block text-sm/6 font-medium text-gray-900 dark:text-gray-300">
                        Description
                      </label>
                      <div className="mt-2">
                        <input
                          type="text"
                          name="description"
                          id="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          className="block w-full rounded-md bg-white dark:bg-gray-700 px-3 py-1.5 text-base text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                          placeholder="Contract description"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="subcontractor" className="block text-sm/6 font-medium text-gray-900 dark:text-gray-300">
                        Subcontractor
                      </label>
                      <div className="mt-2">
                        <input
                          type="text"
                          name="subcontractor"
                          id="subcontractor"
                          value={formData.subcontractor}
                          onChange={handleInputChange}
                          className="block w-full rounded-md bg-white dark:bg-gray-700 px-3 py-1.5 text-base text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                          placeholder="Subcontractor name"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="date" className="block text-sm/6 font-medium text-gray-900 dark:text-gray-300">
                        Date
                      </label>
                      <div className="mt-2">
                        <DateSelector
                          selectedDate={formData.date}
                          onChange={(date) => setFormData({...formData, date})}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm/6 font-medium text-gray-900 dark:text-gray-300">
                        Attachments
                      </label>
                      <div className="mt-2">
                        <FileUploader
                          files={formData.files || []}
                          onChange={handleFilesChange}
                          multiple={true}
                          isReadOnly={false}
                          allowedFileTypes="image/png, image/jpeg, image/gif, application/pdf"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8 flex items-center justify-end gap-x-6">
                    <button
                      type="button"
                      onClick={onClose}
                      className="text-sm/6 font-semibold text-gray-900 dark:text-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                      Save
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

function ContractsList({ contracts = [], shortName, onRefresh, subcontractor }) {
  // Ensure contracts is always an array
  const contractsList = Array.isArray(contracts) ? contracts : [];
  
  const [selectedContract, setSelectedContract] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  
  const handleViewContract = (contract) => {
    console.log("Original contract:", contract);
    
    // Create a copy with an ID if it doesn't have one
    const contractWithId = {...contract};
    
    // If contract doesn't have an ID, generate one
    if (!contractWithId.id) {
      contractWithId.id = contract.id || `existing-contract-${Date.now()}`;
      console.log("Generated ID for existing contract:", contractWithId.id);
    }
    
    console.log("Contract with ID:", contractWithId);
    setSelectedContract(contractWithId);
    setIsCreatingNew(false);
    setShowModal(true);
  };
  
  const handleAddContract = () => {
    setSelectedContract(null);
    setIsCreatingNew(true);
    setShowModal(true);
    console.log("Opening contract modal", showModal); // Debug log
  };
  
  const handleDeleteClick = (e, contract) => {
    e.stopPropagation(); // Prevent triggering the view contract action
    setSelectedContract(contract);
    setShowDeleteConfirm(true);
  };
  
  const handleSaveContract = async (contractData) => {
    try {
      console.log("Saving contract with data:", contractData);
      
      // Make sure files array is defined if it doesn't exist
      if (!contractData.files) {
        contractData.files = [];
      }
      
      // Handle files that need to be deleted on the server (from existing contracts)
      const filesToDelete = contractData.filesToDelete || [];
      
      if (isCreatingNew) {
        // Add new contract
        console.log("Creating new contract");
        const { addScopeContract } = await import('../../utils/api');
        await addScopeContract(shortName, contractData);
      } else {
        // Update existing contract
        console.log("Updating existing contract");
        console.log("Contract ID:", contractData.id);
        console.log("Selected contract:", selectedContract);
        
        // If contract is missing an ID, but we're updating an existing contract,
        // take the ID from the selected contract
        if (!contractData.id && selectedContract && selectedContract.id) {
          console.log("Adding missing ID from selected contract");
          contractData.id = selectedContract.id;
        } else if (!contractData.id) {
          console.error("Contract is missing ID:", contractData);
          throw new Error("Contract ID is missing, cannot update contract");
        }
        
        const { updateScopeContract, deleteFile } = await import('../../utils/api');
        
        // First, update the contract in the database
        const contractId = contractData.id;
        
        // Remove the filesToDelete field from the data before sending to the API
        const contractDataToSave = {...contractData};
        delete contractDataToSave.filesToDelete;
        
        await updateScopeContract(shortName, contractId, contractDataToSave);
        console.log("Contract updated successfully with ID:", contractId);
        
        // Then, if there are files to delete, delete them one by one
        if (filesToDelete.length > 0) {
          console.log("Deleting files:", filesToDelete);
          for (const fileUuid of filesToDelete) {
            try {
              await deleteFile(fileUuid);
              console.log(`Deleted file ${fileUuid}`);
            } catch (fileErr) {
              console.error(`Error deleting file ${fileUuid}:`, fileErr);
              // Continue with other files even if one fails
            }
          }
        }
      }
      
      // Refresh the data
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error saving contract:', error);
      alert(`Error: ${error.message}`);
    }
  };
  
  const handleDeleteContract = async () => {
    try {
      const { deleteScopeContract } = await import('../../utils/api');
      await deleteScopeContract(shortName, selectedContract.id);
      
      // Close the confirmation dialog
      setShowDeleteConfirm(false);
      
      // Refresh the data
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting contract:', error);
      alert(`Error: ${error.message}`);
    }
  };
  
  const hasNoContracts = contractsList.length === 0;

  // Prepare the JSX for just the add button
  const noContractsContent = (
    <div>
      <button
        onClick={handleAddContract}
        className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-500"
      >
        <PlusCircleIcon className="h-5 w-5 mr-1" />
        Add contract
      </button>
    </div>
  );

  // Always return the same structure but conditionally render the list
  return (
    <div>
      {hasNoContracts ? (
        noContractsContent
      ) : (
        <>
          <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
            {contractsList.map((contract, index) => (
              <li 
                key={contract.id || index} 
                className="flex items-center justify-between py-4 pr-5 pl-4 text-sm hover:bg-gray-50 cursor-pointer"
                onClick={() => handleViewContract(contract)}
              >
                <div className="flex w-0 flex-1 items-center">
                  <DocumentTextIcon className="h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" />
                  <div className="ml-4 flex min-w-0 flex-1 gap-2">
                    <span className="truncate font-medium">
                      {contract.description || 'Contract'}
                      {contract.files && contract.files.length > 0 && (
                        <span className="ml-1 inline-flex">
                          <PaperClipIcon className="h-4 w-4 text-gray-500 translate-y-[1px]" aria-hidden="true" />
                          <span className="ml-0.5 text-xs text-gray-500 translate-y-[2px]">{contract.files.length}</span>
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 text-gray-400">{formatDate(contract.date)}</span>
                  </div>
                </div>
                <div className="ml-4 flex items-center space-x-4">
                  <div className="flex-shrink-0 font-medium">
                    {formatCurrency(contract.amount)}
                  </div>
                  <div className="flex space-x-1">
                    <button 
                      className="text-gray-400 hover:text-indigo-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewContract(contract);
                      }}
                      title="Edit contract"
                    >
                      <PencilSquareIcon className="h-5 w-5" />
                    </button>
                    <button 
                      className="text-gray-400 hover:text-red-600"
                      onClick={(e) => handleDeleteClick(e, contract)}
                      title="Delete contract"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          
          <div className="mt-3">
            <button
              onClick={handleAddContract}
              className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-500"
            >
              <PlusCircleIcon className="h-5 w-5 mr-1" />
              Add contract
            </button>
          </div>
        </>
      )}
      
      {/* Contract form modal - Always include this */}
      <ContractModal 
        isOpen={showModal}
        onClose={() => {
          console.log("Closing contract modal");
          setShowModal(false);
        }}
        contract={isCreatingNew ? null : selectedContract}
        onSave={handleSaveContract}
        title={isCreatingNew ? "Add New Contract" : "Edit Contract"}
        shortName={shortName}
        subcontractor={subcontractor}
        isCreatingNew={isCreatingNew}
      />
      
      {/* Delete confirmation dialog */}
      <ConfirmationDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete Contract"
        message={`Are you sure you want to delete contract "${selectedContract?.description || 'this contract'}"? This action cannot be undone.`}
        onConfirm={handleDeleteContract}
        onCancel={() => setShowDeleteConfirm(false)}
        confirmButtonText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-500"
        icon="warning"
      />
    </div>
  );
}

export default ContractsList;